import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { KeyRound, Copy, Check, Download, AlertTriangle, Printer } from "lucide-react";

/**
 * Shows newly generated recovery codes. The user must explicitly confirm
 * "Ich habe die Codes gespeichert" before they can close.
 */
export default function RecoveryCodesDialog({ open, codes, onClose, title }) {
  const [copied, setCopied] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const downloadTxt = () => {
    const blob = new Blob(
      [`VMBS Admin – 2FA Recovery Codes\nErstellt: ${new Date().toLocaleString("de-DE")}\n\n${codes.join("\n")}\n\nJeder Code kann nur einmal verwendet werden. Bewahren Sie ihn sicher auf.\n`],
      { type: "text/plain;charset=utf-8" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vmbs-recovery-codes-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const printPage = () => {
    const html = `<!doctype html><meta charset="utf-8"><title>VMBS 2FA Recovery Codes</title>
      <style>body{font-family:Arial,sans-serif;padding:32px;max-width:600px;margin:auto}
      h1{color:#C5993A}code{font-family:Menlo,monospace;font-size:18px}
      ul{list-style:none;padding:0;display:grid;grid-template-columns:1fr 1fr;gap:12px}
      li{padding:12px;border:1px solid #ccc;border-radius:4px;text-align:center}
      .warn{background:#fff3cd;padding:12px;border-radius:4px;border:1px solid #ffc107;margin:16px 0}</style>
      <h1>VMBS Admin – 2FA Recovery Codes</h1>
      <p>Erstellt am ${new Date().toLocaleString("de-DE")}</p>
      <ul>${codes.map((c) => `<li><code>${c}</code></li>`).join("")}</ul>
      <p class="warn"><strong>Wichtig:</strong> Jeder Code kann nur einmal verwendet werden. Bewahren Sie diesen Ausdruck an einem sicheren Ort auf (z.B. zusammen mit Ihrem Passwort-Tresor).</p>`;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 200);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && confirmed && onClose()}>
      <DialogContent
        className="bg-[#12202f] border-amber-400/40 text-white max-w-xl"
        aria-describedby="recovery-desc"
        onPointerDownOutside={(e) => !confirmed && e.preventDefault()}
        onEscapeKeyDown={(e) => !confirmed && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-amber-400" />
            {title || "Recovery-Codes für 2FA"}
          </DialogTitle>
          <p id="recovery-desc" className="sr-only">Recovery-Codes anzeigen</p>
        </DialogHeader>

        <div className="space-y-4 py-3" data-testid="recovery-codes-content">
          <div className="bg-amber-400/10 border border-amber-400/30 rounded p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-[#F0F4F8]">
              Speichern Sie diese 10 Codes <strong>jetzt</strong> sicher ab. Sie werden nur einmal angezeigt
              und ermöglichen den Login, falls Sie Ihr Smartphone verlieren. Jeder Code ist nur <strong>einmal</strong> nutzbar.
            </div>
          </div>

          <div data-testid="recovery-codes-list" className="grid grid-cols-2 gap-2 bg-[#0C1620] border border-[#3A4F60]/40 rounded p-4">
            {codes.map((code, i) => (
              <code
                key={code}
                data-testid={`recovery-code-${i}`}
                className="block text-center text-[#C5993A] font-mono text-sm py-2 px-3 bg-[#12202f] rounded select-all"
              >
                {code}
              </code>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button data-testid="copy-codes" onClick={copyAll} variant="ghost" size="sm" className="text-[#8899AA] hover:text-[#C5993A] border border-[#3A4F60]/40">
              {copied ? <Check className="w-3.5 h-3.5 mr-1.5 text-green-400" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
              {copied ? "Kopiert" : "Alle kopieren"}
            </Button>
            <Button data-testid="download-codes" onClick={downloadTxt} variant="ghost" size="sm" className="text-[#8899AA] hover:text-[#C5993A] border border-[#3A4F60]/40">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Als .txt
            </Button>
            <Button data-testid="print-codes" onClick={printPage} variant="ghost" size="sm" className="text-[#8899AA] hover:text-[#C5993A] border border-[#3A4F60]/40">
              <Printer className="w-3.5 h-3.5 mr-1.5" /> Drucken
            </Button>
          </div>

          <label className="flex items-start gap-2 pt-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="confirm-saved"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="accent-[#C5993A] w-4 h-4 mt-0.5"
            />
            <span className="text-sm text-[#F0F4F8]">Ich habe die Codes an einem sicheren Ort gespeichert.</span>
          </label>
        </div>

        <DialogFooter>
          <Button
            data-testid="close-recovery-codes"
            onClick={onClose}
            disabled={!confirmed}
            className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0"
          >
            Weiter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
